import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Box, Button, Typography, TextField, InputLabel } from "@mui/material";
import { IFunctionalLocationValues, IFunctionalLocationForm } from "./FunctionalLocationCreationFormInterfaces";
import { functionalLocationFormValidationSchema } from "validations/schemas";
import { useStyles } from "./FunctionalLocationCreationForm.styles";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { FailureDetailsSettingsContext } from "contexts/FailureDetailsSettings/FailureDetailsSettingsContext";
import CheckIcon from "@mui/icons-material/Check";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { LanguageCodeMap } from "constants/constants";

export const FunctionalLocationCreationForm = ({
    handleClose,
    selectedFunctionalLocationToEdit,
}: IFunctionalLocationForm) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { corporations } = useContext(CorporateContext);
    const { selectedAreaId, selectedFunctionalLocationId } = useContext(FailureDetailsSettingsContext);

    const { apolloMutationError, addOrInsertFunctionalLocation } = FailureDetailsEntity.UpsertFunctionalLocation();
    const { getExistingTranslationId } = FailureDetailsEntity.CheckForExistingTranslationId();
    const { updateTranslatioName, apolloMutationError: errorOnTranslationNameUpdate } =
        FailureDetailsEntity.UpdateTranslationName();
    const { insertTranslatioName, apolloMutationError: errorOnTranslationNameInsertion } =
        FailureDetailsEntity.InsertTranslationName();

    const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);

    const languageIdSelected = useMemo(() => LanguageCodeMap[language as keyof typeof LanguageCodeMap], [language]);
    const existingFunctionalLocationTranslationForLanguage = useMemo(
        () =>
            selectedFunctionalLocationToEdit
                ? selectedFunctionalLocationToEdit.translation.translation_names.find(
                      (tn) => tn.language_id === languageIdSelected,
                  )
                : undefined,
        [selectedFunctionalLocationToEdit, languageIdSelected],
    );
    const errorMessage = useMemo(() => {
        let message = "";

        if (
            apolloMutationError?.message.includes("uq_functional_location_name") ||
            apolloMutationError?.message.includes("uq_subordinate_location_name")
        ) {
            message = selectedFunctionalLocationId
                ? t(
                      "failure_details_setup.add_secondary_functional_location_modal.existing_secondary_functional_location_error",
                  )
                : t("failure_details_setup.add_functional_location_modal.existing_functional_location_error");
        } else if (
            errorOnTranslationNameUpdate?.message.includes("translation_name_name_language_id_corporate_id_key") ||
            errorOnTranslationNameInsertion?.message.includes("translation_name_name_language_id_corporate_id_key")
        ) {
            message = selectedFunctionalLocationId
                ? t(
                      "failure_details_setup.add_secondary_functional_location_modal.existing_area_name_translation_error",
                  )
                : t("failure_details_setup.add_functional_location_modal.existing_area_name_translation_error");
        } else if (apolloMutationError || errorOnTranslationNameUpdate || errorOnTranslationNameInsertion) {
            message = t("failure_details_setup.add_functional_location_modal.generic_error");
        }

        return message;
    }, [
        selectedFunctionalLocationId,
        apolloMutationError,
        errorOnTranslationNameUpdate,
        errorOnTranslationNameInsertion,
        t,
    ]);
    const languageCode = language.toLocaleUpperCase();

    const handleAddFunctionalLocationSubmit = useCallback(
        async (values: IFunctionalLocationValues) => {
            const translationNameValues = {
                ...(selectedFunctionalLocationToEdit && {
                    translation_id: selectedFunctionalLocationToEdit.translation.id,
                }),
                language_id: languageIdSelected,
                name: values.name,
                corporate_id: corporations[0].corporate_id,
            };

            if (selectedFunctionalLocationToEdit) {
                try {
                    let response;
                    if (existingFunctionalLocationTranslationForLanguage) {
                        response = await updateTranslatioName({
                            variables: {
                                translation_id: selectedFunctionalLocationToEdit?.translation.id,
                                language_id: languageIdSelected,
                                changes: { name: values.name },
                            },
                        });
                    } else {
                        response = await insertTranslatioName({
                            variables: {
                                translation: translationNameValues,
                            },
                        });
                    }
                    if (response) {
                        handleClose();
                    }
                } catch {
                    return;
                }
            } else {
                const existingTranslationId = await getExistingTranslationId(translationNameValues);
                const functionalLocationValues = {
                    area_id: selectedAreaId,
                    subordinate_to_id: selectedFunctionalLocationId || null,
                    ...(existingTranslationId
                        ? { name_translation_id: existingTranslationId }
                        : {
                              translation: {
                                  data: {
                                      translation_names: {
                                          data: translationNameValues,
                                      },
                                  },
                              },
                          }),
                };
                try {
                    const response = await addOrInsertFunctionalLocation({
                        variables: { functional_location: functionalLocationValues },
                    });
                    if (response) {
                        handleClose();
                    }
                } catch {
                    return;
                }
            }
        },
        [
            addOrInsertFunctionalLocation,
            handleClose,
            selectedAreaId,
            selectedFunctionalLocationId,
            selectedFunctionalLocationToEdit,
            languageIdSelected,
            corporations,
            getExistingTranslationId,
            existingFunctionalLocationTranslationForLanguage,
            insertTranslatioName,
            updateTranslatioName,
        ],
    );

    const initFormValues: IFunctionalLocationValues = {
        name:
            selectedFunctionalLocationToEdit && existingFunctionalLocationTranslationForLanguage
                ? existingFunctionalLocationTranslationForLanguage.name
                : "",
        area_id: selectedAreaId!,
        subordinate_to_id: selectedFunctionalLocationId || null,
    };

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={functionalLocationFormValidationSchema}
            onSubmit={handleAddFunctionalLocationSubmit}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-functional-location-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {`${t(
                                        "failure_details_setup.add_functional_location_modal.name",
                                    )} (${languageCode})`}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-functional-location-name-label"
                                placeholder={
                                    selectedFunctionalLocationToEdit &&
                                    !existingFunctionalLocationTranslationForLanguage
                                        ? t("failure_details_setup.missing_translation_placeholder")
                                        : ""
                                }
                                fullWidth
                                id="outlined-functional-location-name-input"
                                className={classes.textField}
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                        </Box>
                        <p
                            style={{ display: errorMessage ? "block" : "none", color: "red" }}
                            className={classes.errorText}
                        >
                            {errorMessage}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t("failure_details_setup.add_functional_location_modal.cancel")}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {<CheckIcon className={classes.icon} />}
                                {t("failure_details_setup.add_functional_location_modal.save")}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
